import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { 
  ArrowLeft, 
  Search, 
  Plus, 
  Edit3, 
  Trash2, 
  Upload, 
  Download, 
  Eye,
  Filter,
  BarChart3,
  FileText,
  Book,
  Star,
  AlertCircle,
  CheckCircle,
  X,
  Save,
  Loader,
  Database
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OddunManagement = ({ onBack }) => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [odduns, setOdduns] = useState([]);
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    has_data: null,
    is_meji: null,
    position: null
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedOddun, setSelectedOddun] = useState(null);
  const [formData, setFormData] = useState({
    position: '',
    name: '',
    traditional_subtitle: '',
    yoruba_name: '',
    binary_pattern: '',
    display_pattern: '',
    meaning: '',
    element: '',
    direction: '',
    colors: [],
    rezo: '',
    rezo_personalizado: '',
    suyere: '',
    en_este_oddun_nace: [],
    descripcion: '',
    consejos_espirituales: [],
    taboos: [],
    ofrendas: [],
    refranes: [],
    patakines: [],
    obras: [],
    rezos_adicionales: [],
    is_meji: false,
    is_combination: false,
    data_source: 'manual'
  });
  const [bulkImportData, setBulkImportData] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDataSources, setShowDataSources] = useState(false);
  const [dataSources, setDataSources] = useState({});

  useEffect(() => {
    fetchOdduns();
    fetchStats();
  }, [currentPage, searchQuery, filters]);

  const fetchOdduns = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      
      if (filters.has_data !== null) {
        params.append('has_data', filters.has_data.toString());
      }
      
      if (filters.is_meji !== null) {
        params.append('is_meji', filters.is_meji.toString());
      }
      
      if (filters.position) {
        params.append('position', filters.position.toString());
      }

      const response = await axios.get(`${API}/admin/odduns?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOdduns(response.data.odduns || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      toast.error('Error al cargar los odduns');
      console.error('Error fetching odduns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/odduns/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDataSources = async () => {
    try {
      const response = await axios.get(`${API}/admin/odduns/data-sources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataSources(response.data);
      setShowDataSources(true);
      toast.success('Información de fuentes de datos cargada');
    } catch (error) {
      console.error('Error fetching data sources:', error);
      toast.error('Error al cargar fuentes de datos');
    }
  };

  const handleCreateOddun = async () => {
    try {
      setIsProcessing(true);
      
      // Validate required fields
      if (!formData.position || !formData.name || !formData.binary_pattern) {
        toast.error('Los campos Posición, Nombre y Patrón Binario son obligatorios');
        return;
      }

      const response = await axios.post(`${API}/admin/odduns`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Oddun creado exitosamente');
      setShowCreateModal(false);
      resetForm();
      fetchOdduns();
      fetchStats();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error(`Ya existe un oddun en la posición ${formData.position}`);
      } else {
        toast.error('Error al crear el oddun');
      }
      console.error('Error creating oddun:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateOddun = async () => {
    try {
      setIsProcessing(true);
      
      const response = await axios.put(`${API}/admin/odduns/${selectedOddun.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Oddun actualizado exitosamente');
      setShowEditModal(false);
      setSelectedOddun(null);
      resetForm();
      fetchOdduns();
    } catch (error) {
      toast.error('Error al actualizar el oddun');
      console.error('Error updating oddun:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteOddun = async (oddun) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar el oddun "${oddun.name}" (Posición ${oddun.position})?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/odduns/${oddun.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Oddun eliminado exitosamente');
      fetchOdduns();
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar el oddun');
      console.error('Error deleting oddun:', error);
    }
  };

  const handleBulkImport = async () => {
    try {
      setIsProcessing(true);
      
      if (!bulkImportData.trim()) {
        toast.error('Por favor, ingresa los datos para importar');
        return;
      }

      let odduns;
      try {
        odduns = JSON.parse(bulkImportData);
      } catch (e) {
        toast.error('El formato de datos no es válido. Debe ser JSON válido.');
        return;
      }

      if (!Array.isArray(odduns)) {
        toast.error('Los datos deben ser un array de odduns');
        return;
      }

      const response = await axios.post(`${API}/admin/odduns/bulk-import`, {
        odduns: odduns,
        overwrite_existing: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const summary = response.data.summary;
      toast.success(`Importación completada: ${summary.created} creados, ${summary.updated} actualizados`);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Import errors:', response.data.errors);
      }

      setShowBulkImport(false);
      setBulkImportData('');
      fetchOdduns();
      fetchStats();
    } catch (error) {
      toast.error('Error durante la importación masiva');
      console.error('Error bulk importing:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      position: '',
      name: '',
      traditional_subtitle: '',
      yoruba_name: '',
      binary_pattern: '',
      display_pattern: '',
      meaning: '',
      element: '',
      direction: '',
      colors: [],
      rezo: '',
      suyere: '',
      en_este_oddun_nace: [],
      descripcion: '',
      consejos_espirituales: [],
      taboos: [],
      ofrendas: [],
      refranes: [],
      patakines: [],
      obras: [],
      rezos_adicionales: [],
      is_meji: false,
      is_combination: false,
      data_source: 'manual'
    });
  };

  const openEditModal = (oddun) => {
    setSelectedOddun(oddun);
    setFormData({
      position: oddun.position || '',
      name: oddun.name || '',
      traditional_subtitle: oddun.traditional_subtitle || '',
      yoruba_name: oddun.yoruba_name || '',
      binary_pattern: oddun.binary_pattern || '',
      display_pattern: oddun.display_pattern || '',
      meaning: oddun.meaning || '',
      element: oddun.element || '',
      direction: oddun.direction || '',
      colors: oddun.colors || [],
      rezo: oddun.rezo || '',
      rezo_personalizado: oddun.rezo_personalizado || '',
      suyere: oddun.suyere || '',
      en_este_oddun_nace: oddun.en_este_oddun_nace || [],
      descripcion: oddun.descripcion || '',
      consejos_espirituales: oddun.consejos_espirituales || [],
      taboos: oddun.taboos || [],
      ofrendas: oddun.ofrendas || [],
      refranes: oddun.refranes || [],
      patakines: oddun.patakines || [],
      obras: oddun.obras || [],
      rezos_adicionales: oddun.rezos_adicionales || [],
      is_meji: oddun.is_meji || false,
      is_combination: oddun.is_combination || false,
      data_source: oddun.data_source || 'manual'
    });
    setShowEditModal(true);
  };

  const openViewModal = (oddun) => {
    setSelectedOddun(oddun);
    setShowViewModal(true);
  };

  // Helper functions for data source display
  const getDataSourceLabel = (source) => {
    const labels = {
      'comprehensive_traditional': 'Datos Tradicionales Completos',
      'ODDUNS1_traditional_authentic': 'ODDUNS1.txt (Enciclopedia Tradicional)',
      'ODDUNS2_traditional_authentic': 'ODDUNS2.txt (Enciclopedia Tradicional)', 
      'excel_corrected': 'Archivo Excel Oficial',
      'generated': 'Generado Automáticamente',
      'manual': 'Ingreso Manual'
    };
    return labels[source] || source || 'Fuente Desconocida';
  };

  const getDataSourceStyle = (source) => {
    const styles = {
      'comprehensive_traditional': 'bg-green-100 text-green-800',
      'ODDUNS1_traditional_authentic': 'bg-purple-100 text-purple-800',
      'ODDUNS2_traditional_authentic': 'bg-purple-100 text-purple-800',
      'excel_corrected': 'bg-blue-100 text-blue-800',
      'generated': 'bg-gray-100 text-gray-800',
      'manual': 'bg-yellow-100 text-yellow-800'
    };
    return styles[source] || 'bg-gray-100 text-gray-800';
  };

  const getCompletudeStyle = (percentage) => {
    if (percentage >= 80) return 'bg-green-100 text-green-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    if (percentage >= 20) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getDataSourceBreakdown = (oddun) => {
    const breakdown = [];
    
    if (oddun.name) {
      breakdown.push(`• Nombre: ${getDataSourceForField(oddun, 'name')}`);
    }
    if (oddun.binary_pattern) {
      breakdown.push(`• Patrón Binario: ${getDataSourceForField(oddun, 'binary_pattern')}`);
    }
    if (oddun.consejos_espirituales && oddun.consejos_espirituales.length > 0) {
      breakdown.push(`• Consejos Espirituales (${oddun.consejos_espirituales.length}): ${getDataSourceForField(oddun, 'consejos_espirituales')}`);
    }
    if (oddun.taboos && oddun.taboos.length > 0) {
      breakdown.push(`• Tabúes (${oddun.taboos.length}): ${getDataSourceForField(oddun, 'taboos')}`);
    }
    if (oddun.ofrendas && oddun.ofrendas.length > 0) {
      breakdown.push(`• Ofrendas (${oddun.ofrendas.length}): ${getDataSourceForField(oddun, 'ofrendas')}`);
    }
    if (oddun.refranes && oddun.refranes.length > 0) {
      breakdown.push(`• Refranes (${oddun.refranes.length}): ${getDataSourceForField(oddun, 'refranes')}`);
    }
    if (oddun.patakines && oddun.patakines.length > 0) {
      breakdown.push(`• Patakines (${oddun.patakines.length}): ${getDataSourceForField(oddun, 'patakines')}`);
    }
    if (oddun.rezo || oddun.rezo_principal) {
      breakdown.push(`• Rezos: ${getDataSourceForField(oddun, 'rezo')}`);
    }
    
    return breakdown.map((item, index) => (
      <div key={index}>{item}</div>
    ));
  };

  const getDataSourceForField = (oddun, field) => {
    // This would ideally come from detailed field-level metadata
    // For now, we'll use the main data source as a fallback
    const source = oddun.data_source || 'unknown';
    
    // Provide more specific information based on known patterns
    if (source.includes('ODDUNS1')) return 'ODDUNS1.txt';
    if (source.includes('ODDUNS2')) return 'ODDUNS2.txt';
    if (source.includes('excel')) return 'Excel Oficial';
    if (source.includes('comprehensive')) return 'Base Tradicional';
    if (source.includes('generated')) return 'Generado';
    
    return getDataSourceLabel(source);
  };

  const getDataSourceAbbreviation = (source) => {
    const abbreviations = {
      'comprehensive_traditional': 'TRAD',
      'ODDUNS1_traditional_authentic': 'ODD1',
      'ODDUNS2_traditional_authentic': 'ODD2',
      'excel_corrected': 'EXCEL',
      'generated': 'GEN',
      'manual': 'MAN'
    };
    return abbreviations[source] || 'DESC';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 bg-white hover:bg-gray-50 px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver
            </button>
            <div>
              <h1 className="font-cinzel text-3xl font-bold text-emerald-800">
                Gestión de Odduns
              </h1>
              <p className="text-emerald-600">
                Administra la base de datos de signos de Ifá
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => fetchStats()}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <BarChart3 className="w-5 h-5" />
              Estadísticas
            </button>
            <button
              onClick={() => fetchDataSources()}
              className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <Database className="w-5 h-5" />
              Fuentes de Datos
            </button>
            <button
              onClick={() => setShowBulkImport(true)}
              className="flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <Upload className="w-5 h-5" />
              Importar
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-xl shadow-md transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              Nuevo Oddun
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Odduns</p>
                <p className="text-2xl font-bold text-emerald-800">{stats.total_odduns || 0}</p>
              </div>
              <Book className="w-8 h-8 text-emerald-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Odduns Meji</p>
                <p className="text-2xl font-bold text-blue-800">{stats.meji_odduns || 0}</p>
              </div>
              <Star className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Con Datos</p>
                <p className="text-2xl font-bold text-green-800">{stats.odduns_with_data || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Completado</p>
                <p className="text-2xl font-bold text-purple-800">{stats.completion_percentage || 0}%</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl p-6 shadow-lg mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, subtítulo o nombre yoruba..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filters.has_data === null ? '' : filters.has_data.toString()}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  has_data: e.target.value === '' ? null : e.target.value === 'true' 
                }))}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Todos los datos</option>
                <option value="true">Con datos</option>
                <option value="false">Sin datos</option>
              </select>
              
              <select
                value={filters.is_meji === null ? '' : filters.is_meji.toString()}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  is_meji: e.target.value === '' ? null : e.target.value === 'true' 
                }))}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Todos los tipos</option>
                <option value="true">Solo Meji</option>
                <option value="false">Solo Combinaciones</option>
              </select>
              
              <input
                type="number"
                placeholder="Posición"
                value={filters.position || ''}
                onChange={(e) => setFilters(prev => ({ 
                  ...prev, 
                  position: e.target.value ? parseInt(e.target.value) : null 
                }))}
                min="1"
                max="256"
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-32"
              />
            </div>
          </div>
        </div>

        {/* Odduns Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Posición</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Nombre</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Subtítulo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Patrón</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Tipo</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Datos</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Fuente</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-emerald-800">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <Loader className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
                      <p className="text-gray-600 mt-2">Cargando odduns...</p>
                    </td>
                  </tr>
                ) : odduns.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <AlertCircle className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">No se encontraron odduns</p>
                    </td>
                  </tr>
                ) : (
                  odduns.map((oddun) => (
                    <tr key={oddun.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{oddun.position}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{oddun.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{oddun.traditional_subtitle || '-'}</td>
                      <td className="px-6 py-4 text-sm font-mono text-gray-700">{oddun.binary_pattern}</td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          oddun.is_meji 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {oddun.is_meji ? 'Meji' : 'Combinación'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          oddun.has_data 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {oddun.has_data ? 'Completo' : 'Básico'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getDataSourceStyle(oddun.data_source)}`}>
                          {getDataSourceAbbreviation(oddun.data_source)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openViewModal(oddun)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver detalles"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(oddun)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteOddun(oddun)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Página {currentPage} de {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800">
                    {showCreateModal ? 'Crear Nuevo Oddun' : 'Editar Oddun'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setSelectedOddun(null);
                      resetForm();
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Posición *
                    </label>
                    <input
                      type="number"
                      value={formData.position}
                      onChange={(e) => setFormData(prev => ({ ...prev, position: parseInt(e.target.value) || '' }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      min="1"
                      max="999"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtítulo Tradicional
                    </label>
                    <input
                      type="text"
                      value={formData.traditional_subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, traditional_subtitle: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre Yoruba
                    </label>
                    <input
                      type="text"
                      value={formData.yoruba_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, yoruba_name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Patrón Binario * (8 dígitos)
                    </label>
                    <input
                      type="text"
                      value={formData.binary_pattern}
                      onChange={(e) => setFormData(prev => ({ ...prev, binary_pattern: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono"
                      pattern="[01]{8}"
                      maxLength="8"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Elemento
                    </label>
                    <input
                      type="text"
                      value={formData.element}
                      onChange={(e) => setFormData(prev => ({ ...prev, element: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dirección
                    </label>
                    <input
                      type="text"
                      value={formData.direction}
                      onChange={(e) => setFormData(prev => ({ ...prev, direction: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuente de Datos
                    </label>
                    <select
                      value={formData.data_source}
                      onChange={(e) => setFormData(prev => ({ ...prev, data_source: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="manual">Manual</option>
                      <option value="ODDUNS2.txt">ODDUNS2.txt</option>
                      <option value="excel">Excel</option>
                      <option value="tradicional">Tradicional</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Significado
                  </label>
                  <textarea
                    value={formData.meaning}
                    onChange={(e) => setFormData(prev => ({ ...prev, meaning: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    rows="3"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_meji}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_meji: e.target.checked }))}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Es Meji</span>
                  </label>
                  
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_combination}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_combination: e.target.checked }))}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Es Combinación</span>
                  </label>
                </div>

                {/* Extended Fields for detailed content */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="font-semibold text-lg text-gray-800 mb-4">Contenido Detallado</h4>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rezo
                      </label>
                      <textarea
                        value={formData.rezo}
                        onChange={(e) => setFormData(prev => ({ ...prev, rezo: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        rows="4"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rezo Personalizado
                      </label>
                      <textarea
                        value={formData.rezo_personalizado}
                        onChange={(e) => setFormData(prev => ({ ...prev, rezo_personalizado: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        rows="4"
                        placeholder="Rezo personalizado para este oddun..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Suyere
                      </label>
                      <textarea
                        value={formData.suyere}
                        onChange={(e) => setFormData(prev => ({ ...prev, suyere: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        rows="4"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descripción
                    </label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      rows="6"
                    />
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedOddun(null);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={showCreateModal ? handleCreateOddun : handleUpdateOddun}
                  disabled={isProcessing}
                  className="flex-1 bg-emerald-500 text-white py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      {showCreateModal ? 'Crear' : 'Actualizar'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedOddun && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800">
                    {selectedOddun.name} - Posición {selectedOddun.position}
                  </h3>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedOddun(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Información Básica</h4>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                        <p><span className="font-medium">Nombre:</span> {selectedOddun.name}</p>
                        {selectedOddun.traditional_subtitle && (
                          <p><span className="font-medium">Subtítulo:</span> {selectedOddun.traditional_subtitle}</p>
                        )}
                        {selectedOddun.yoruba_name && (
                          <p><span className="font-medium">Nombre Yoruba:</span> {selectedOddun.yoruba_name}</p>
                        )}
                        <p><span className="font-medium">Patrón Binario:</span> <code className="bg-white px-2 py-1 rounded">{selectedOddun.binary_pattern}</code></p>
                        {selectedOddun.element && (
                          <p><span className="font-medium">Elemento:</span> {selectedOddun.element}</p>
                        )}
                        {selectedOddun.direction && (
                          <p><span className="font-medium">Dirección:</span> {selectedOddun.direction}</p>
                        )}
                      </div>
                    </div>

                    {/* Data Source Information - Admin Only */}
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <span className="text-blue-600">🔍</span>
                        Origen de Datos (Solo Admin)
                      </h4>
                      <div className="bg-blue-50 rounded-xl p-4 space-y-2">
                        <p><span className="font-medium">Fuente Principal:</span> 
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getDataSourceStyle(selectedOddun.data_source)}`}>
                            {getDataSourceLabel(selectedOddun.data_source)}
                          </span>
                        </p>
                        {selectedOddun.fuente && (
                          <p><span className="font-medium">Documento:</span> {selectedOddun.fuente}</p>
                        )}
                        {selectedOddun.fecha_actualizacion && (
                          <p><span className="font-medium">Última Actualización:</span> {selectedOddun.fecha_actualizacion}</p>
                        )}
                        <p><span className="font-medium">Nivel de Completitud:</span> 
                          <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getCompletudeStyle(selectedOddun.completion_percentage || 0)}`}>
                            {selectedOddun.completion_percentage || 0}%
                          </span>
                        </p>
                        <div className="mt-3">
                          <p className="font-medium text-sm mb-2">Desglose de Fuentes de Datos:</p>
                          <div className="text-xs space-y-1 text-gray-600">
                            {getDataSourceBreakdown(selectedOddun)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Clasificación</h4>
                      <div className="flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          selectedOddun.is_meji 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {selectedOddun.is_meji ? 'Meji' : 'Combinación'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          selectedOddun.has_data 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedOddun.has_data ? 'Datos Completos' : 'Datos Básicos'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {selectedOddun.meaning && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Significado</h4>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <p className="text-gray-700">{selectedOddun.meaning}</p>
                        </div>
                      </div>
                    )}

                    {selectedOddun.colors && selectedOddun.colors.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Colores</h4>
                        <div className="bg-gray-50 rounded-xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedOddun.colors.map((color, index) => (
                              <span key={index} className="px-3 py-1 bg-white rounded-full text-sm border">
                                {color}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {selectedOddun.descripcion && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Descripción</h4>
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-gray-700 whitespace-pre-wrap">{selectedOddun.descripcion}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-6">
                  {selectedOddun.rezo && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Rezo</h4>
                      <div className="bg-yellow-50 rounded-xl p-4">
                        <p className="text-gray-700 font-mono text-sm whitespace-pre-wrap">{selectedOddun.rezo}</p>
                      </div>
                    </div>
                  )}

                  {selectedOddun.rezo_personalizado && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Rezo Personalizado</h4>
                      <div className="bg-emerald-50 rounded-xl p-4">
                        <p className="text-gray-700 font-mono text-sm whitespace-pre-wrap">{selectedOddun.rezo_personalizado}</p>
                      </div>
                    </div>
                  )}

                  {selectedOddun.suyere && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Suyere</h4>
                      <div className="bg-blue-50 rounded-xl p-4">
                        <p className="text-gray-700 font-mono text-sm whitespace-pre-wrap">{selectedOddun.suyere}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrays of content */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedOddun.consejos_espirituales && selectedOddun.consejos_espirituales.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Consejos Espirituales</h4>
                      <div className="bg-green-50 rounded-xl p-4">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {selectedOddun.consejos_espirituales.map((consejo, index) => (
                            <li key={index}>{consejo}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {selectedOddun.taboos && selectedOddun.taboos.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Tabúes</h4>
                      <div className="bg-red-50 rounded-xl p-4">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {selectedOddun.taboos.map((taboo, index) => (
                            <li key={index}>{taboo}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {selectedOddun.ofrendas && selectedOddun.ofrendas.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Ofrendas</h4>
                      <div className="bg-purple-50 rounded-xl p-4">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {selectedOddun.ofrendas.map((ofrenda, index) => (
                            <li key={index}>{ofrenda}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {selectedOddun.refranes && selectedOddun.refranes.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-2">Refranes</h4>
                      <div className="bg-orange-50 rounded-xl p-4">
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                          {selectedOddun.refranes.map((refran, index) => (
                            <li key={index} className="italic">"{refran}"</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                  <p>Fuente: {selectedOddun.data_source}</p>
                  {selectedOddun.created_at && (
                    <p>Creado: {new Date(selectedOddun.created_at).toLocaleDateString()}</p>
                  )}
                  {selectedOddun.updated_at && (
                    <p>Actualizado: {new Date(selectedOddun.updated_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Import Modal */}
        {showBulkImport && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800">
                    Importación Masiva de Odduns
                  </h3>
                  <button
                    onClick={() => {
                      setShowBulkImport(false);
                      setBulkImportData('');
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">Instrucciones</h4>
                  <p className="text-blue-700 text-sm">
                    Pega aquí los datos de los odduns en formato JSON. Debe ser un array de objetos con la estructura del oddun.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Datos JSON
                  </label>
                  <textarea
                    value={bulkImportData}
                    onChange={(e) => setBulkImportData(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                    rows="20"
                    placeholder={`[
  {
    "position": 1,
    "name": "BABA EJIOGBE",
    "traditional_subtitle": "EJIOGBE",
    "binary_pattern": "11111111",
    "meaning": "El padre de todos los odduns",
    "is_meji": true,
    "data_source": "ODDUNS2.txt"
  }
]`}
                  />
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-4">
                <button
                  onClick={() => {
                    setShowBulkImport(false);
                    setBulkImportData('');
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={isProcessing || !bulkImportData.trim()}
                  className="flex-1 bg-emerald-500 text-white py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Importando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      Importar Odduns
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Data Sources Modal */}
        {showDataSources && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-cinzel text-2xl font-bold text-emerald-800">
                    🔍 Fuentes de Datos - Análisis Detallado
                  </h3>
                  <button
                    onClick={() => setShowDataSources(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Summary */}
                {dataSources.summary && (
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6">
                    <h4 className="font-semibold text-lg text-emerald-800 mb-4">Resumen General</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-emerald-700">{dataSources.summary.total_odduns}</div>
                        <div className="text-sm text-emerald-600">Total Odduns</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-700">{dataSources.summary.sources_count}</div>
                        <div className="text-sm text-blue-600">Fuentes de Datos</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-700">
                          {dataSources.summary.highest_quality_source ? 
                            getDataSourceAbbreviation(dataSources.summary.highest_quality_source) : 'N/A'}
                        </div>
                        <div className="text-sm text-purple-600">Fuente de Mayor Calidad</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sources Breakdown */}
                {dataSources.sources_breakdown && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-lg text-gray-800">Desglose por Fuente de Datos</h4>
                    
                    {Object.entries(dataSources.sources_breakdown).map(([sourceKey, sourceData]) => (
                      <div key={sourceKey} className="bg-white border rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h5 className="font-semibold text-lg flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDataSourceStyle(sourceKey)}`}>
                                {getDataSourceAbbreviation(sourceKey)}
                              </span>
                              {sourceData.info.label}
                            </h5>
                            <p className="text-gray-600 text-sm mt-1">{sourceData.info.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-800">{sourceData.count}</div>
                            <div className="text-sm text-gray-600">odduns</div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-lg font-semibold text-green-700">{sourceData.with_data}</div>
                            <div className="text-sm text-gray-600">Con Datos Completos</div>
                            <div className="text-xs text-gray-500">
                              {sourceData.count > 0 ? Math.round((sourceData.with_data / sourceData.count) * 100) : 0}% del total
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-lg font-semibold text-blue-700">{sourceData.avg_completion}%</div>
                            <div className="text-sm text-gray-600">Completitud Promedio</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-lg font-semibold text-purple-700">#{sourceData.info.priority}</div>
                            <div className="text-sm text-gray-600">Prioridad de Fuente</div>
                          </div>
                        </div>

                        {/* Sample odduns */}
                        <div>
                          <h6 className="font-medium text-gray-800 mb-2">Odduns de Ejemplo:</h6>
                          <div className="flex flex-wrap gap-2">
                            {sourceData.odduns.slice(0, 10).map((oddun) => (
                              <span 
                                key={oddun.position}
                                className="px-2 py-1 bg-gray-100 rounded text-xs"
                                title={`${oddun.name} - ${oddun.completion_percentage}% completo`}
                              >
                                {oddun.position}. {oddun.name}
                              </span>
                            ))}
                            {sourceData.odduns.length > 10 && (
                              <span className="px-2 py-1 bg-gray-200 rounded text-xs">
                                +{sourceData.odduns.length - 10} más
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="bg-blue-50 rounded-xl p-4">
                  <h5 className="font-semibold text-blue-800 mb-2">ℹ️ Información para Administradores</h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>TRAD:</strong> Datos tradicionales completos con información espiritual auténtica</li>
                    <li>• <strong>ODD1/ODD2:</strong> Tratados enciclopédicos de Ifá con contenido tradicional verificado</li>
                    <li>• <strong>EXCEL:</strong> Nombres oficiales y patrones binarios del archivo maestro</li>
                    <li>• <strong>GEN:</strong> Estructura base generada automáticamente, requiere enriquecimiento</li>
                    <li>• <strong>MAN:</strong> Datos ingresados manualmente por administradores</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OddunManagement;